import { QuizData, StudentQuiz } from "@/models/quiz.model";
import { QuizRest } from "@/services/rest/quiz.rest";
import { defineStore } from "pinia";
import { lastValueFrom, take } from "rxjs";
import { instance } from "..";
import { CourseStore } from "../course";
import { useChapterStore } from "./chapter";
import { ELoader, useLoaderStore } from "./loader";
import { useNavigationStore } from "./navigation";

export const store = defineStore({
  id: "quiz",
  state: () => ({
    open: false as boolean,
    quizData: {} as QuizData,
    quizIntro: {} as StudentQuiz,
    quizHasEnded: false as boolean,
  }),
  getters: {
    getOpen: (state) => state.open,
    getQuizHasEnded: (state) => state.quizHasEnded,
  },
  actions: {
    setOpen(value: boolean) {
      this.open = value;
    },

    setQuizHasEnded(value: boolean) {
      this.quizHasEnded = value;
    },

    getQuizByChapter() {
      const quiz$ = new QuizRest().getQuizByLearningObject(
        useChapterStore.currentChapter?.learningObjectiveIdForQuiz!
      );
      lastValueFrom(quiz$).then((response) => (this.quizIntro = response));
    },

    setChapterDone() {
      useChapterStore.chapters[useChapterStore.currentChapterIndex].done = true;
    },

    quizEnded(event: { value: boolean }): boolean {
      this.setQuizHasEnded(event.value ?? true);

      CourseStore.quiz.pipe(take(1)).subscribe({
        next: async (quiz) => {
          if (quiz.percentage < 60) return;

          useLoaderStore.stop(ELoader.TRAILS);
          this.quizIntro.done = quiz.percentage >= 60;
          await this.setChapterDone();
          setTimeout(() => useNavigationStore.next(), 250);
        },
      });
      return this.getQuizHasEnded;
    },

    returnQuestionsByQuiz() {
      useLoaderStore.start(ELoader.QUIZ);
      const classroomId = localStorage.getItem("classId") ?? undefined;

      const questions$ = new QuizRest().returnQuestionsByQuiz(
        useChapterStore.currentChapter?.learningObjectiveIdForQuiz!,
        { classroomId }
      );

      lastValueFrom(questions$)
        .then((response) => {
          this.quizData = {
            ...response,
            questionList: [
              ...response.questionList,
              ...response.absQuestionsList,
            ],
          };

          this.setOpen(true);
        })
        .catch(() => {
          this.closeQuiz();
        })
        .finally(() => {
          useLoaderStore.stop(ELoader.QUIZ);
        });
    },

    proceedQuiz(type: string) {
      this.closeQuiz();
    },

    closeQuiz() {
      this.setOpen(false);
      this.setQuizHasEnded(false);
    },

    onShowQuiz() {
      this.getQuizByChapter();
      this.returnQuestionsByQuiz();
    },
  },
});

export const useQuizStore = store(instance);
